import * as React from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import type { ScheduleTypeEditDataType } from 'api/schedule_types'

import { selectTenantsStatus } from 'slices/tenantsSlice'

import type { ScheduleTypeEditData } from 'components/Workspaces/types'
import { connectionTypes } from 'components/common/utils'

// dataConnectionInfoを利用しているため、一度getDataConnectionInfo()を実行する必要あり
const useScheduleType = () => {
  const [editData, setEditData] = React.useState<ScheduleTypeEditData | undefined>()
  const [nameValidity, setNameValidity] = React.useState(false)
  const [targetDatabaseValidity, setTargetDatabaseValidity] = React.useState(false)
  const [targetColumnValidity, setTargetColumnValidity] = React.useState(false)
  const [unitValidity, setUnitValidity] = React.useState(false)
  const [defaultPerformanceIndexValidity, setDefaultPerformanceIndexValidity] = React.useState(false)

  const { dataConnectionInfo } = useSelector(selectTenantsStatus, shallowEqual)

  const disabled = React.useMemo(() => {
    if (!editData) {
      return true
    }

    if (editData.connectionType === connectionTypes.Manual) {
      return !(
        editData.name &&
        nameValidity &&
        editData.unit &&
        unitValidity &&
        editData.defaultPerformanceIndex &&
        defaultPerformanceIndexValidity
      )
    }

    if (editData.connectionType === connectionTypes.Auto) {
      return !(
        editData.name &&
        nameValidity &&
        editData.targetDatabase &&
        targetDatabaseValidity &&
        editData.targetColumn &&
        targetColumnValidity &&
        editData.unit &&
        unitValidity &&
        editData.defaultPerformanceIndex &&
        defaultPerformanceIndexValidity
      )
    }

    return !(editData.name && nameValidity)
  }, [
    editData,
    nameValidity,
    targetDatabaseValidity,
    targetColumnValidity,
    unitValidity,
    defaultPerformanceIndexValidity,
  ])

  const scheduleTypeUpdateData = React.useMemo(() => {
    if (!editData) {
      return undefined
    }
    // key, valueが-の値を除外する
    const newFilter = editData.filter
      .map(parent => ({
        ...parent,
        data: parent.data.filter(row => row.key !== '-' && row.value !== '-'),
      }))
      .filter(parent => parent.data.length !== 0)
    const { dataConnection } = editData

    const data: ScheduleTypeEditDataType = {
      name: editData.name || '',
      color: editData.color,
      requiredSkillIds: editData.requiredSkills.map(skill => skill.skillId),
      dataConnection,
      connectionType: editData.connectionType,
      targetDatabase: (dataConnection && editData.targetDatabase) || null,
      targetColumn: (dataConnection && editData.targetColumn) || null,
      unit: (dataConnection && editData.unit) || null,
      filter: dataConnection ? newFilter : [],
      defaultPerformanceIndex: dataConnection ? Number(editData?.defaultPerformanceIndex) : null,
      isKey: editData.isKey,
      isCostless: editData.isCostless,
    }
    return data
  }, [editData])
  const targetDatabases = React.useMemo(() => {
    if (!dataConnectionInfo) {
      return []
    }
    return dataConnectionInfo.data.map(info => ({
      key: info.targetDatabase.databaseName,
      value: info.targetDatabase.displayName,
    }))
  }, [dataConnectionInfo])
  const targetColumns = React.useMemo(() => {
    if (!dataConnectionInfo || !editData?.targetDatabase) {
      return []
    }
    return dataConnectionInfo.data
      .filter(info => info.targetDatabase.databaseName === editData?.targetDatabase)
      .flatMap(info =>
        info.targetColumns.map(targetColumn => ({
          key: targetColumn.columnName,
          value: targetColumn.displayName,
        }))
      )
  }, [dataConnectionInfo, editData?.targetDatabase])
  const filterColumns = React.useMemo(() => {
    if (!dataConnectionInfo || !editData?.targetDatabase) {
      return []
    }
    return dataConnectionInfo.data
      .filter(info => info.targetDatabase.databaseName === editData?.targetDatabase)
      .flatMap(info => info.filterColumns)
  }, [dataConnectionInfo, editData?.targetDatabase])
  return {
    setNameValidity,
    setTargetDatabaseValidity,
    setTargetColumnValidity,
    setUnitValidity,
    setDefaultPerformanceIndexValidity,
    disabled,
    editData,
    setEditData,
    scheduleTypeUpdateData,
    targetDatabases,
    targetColumns,
    filterColumns,
  }
}
export default useScheduleType
